import { useState } from 'react';
import useLocalStorage from '@/hooks/useLocalStorage';
import { defaultSiteTexts, type SiteTexts } from '@/mocks/adminData';

function TextsPage() {
  const [texts, setTexts] = useLocalStorage<SiteTexts>('admin_site_texts', defaultSiteTexts);
  const [savedFlash, setSavedFlash] = useState(false);

  const update = (partial: Partial<SiteTexts>) => {
    setTexts((prev) => ({ ...prev, ...partial }));
  };

  const handleSave = () => {
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 2000);
  };

  const Field = ({ label, value, onChange, textarea = false, rows = 3 }: { label: string; value: string; onChange: (v: string) => void; textarea?: boolean; rows?: number }) => (
    <div>
      <label className="block text-xs font-medium text-foreground-700 mb-1.5">{label}</label>
      {textarea ? (
        <textarea value={value} onChange={(e) => { if (e.target.value.length <= 500) onChange(e.target.value); }} rows={rows} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-white text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none" />
      ) : (
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-white text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" />
      )}
    </div>
  );

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {savedFlash && (
        <div className="fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm font-medium shadow-sm">
          <i className="ri-check-line mr-1.5" />Alterações salvas com sucesso.
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">Textos do site</h1>
          <p className="text-sm text-foreground-500 mt-1">Edite os principais textos exibidos no site.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hero */}
        <div className="bg-white rounded-xl border border-background-200 p-5 lg:col-span-2">
          <h2 className="font-heading text-base font-semibold text-foreground-900 mb-4 flex items-center gap-2">
            <i className="ri-home-4-line text-primary-600" /> Hero principal
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Título principal" value={texts.heroTitle} onChange={(v) => update({ heroTitle: v })} />
            <Field label="Subtítulo" value={texts.heroSubtitle} onChange={(v) => update({ heroSubtitle: v })} />
            <Field label="Texto do botão principal" value={texts.heroPrimaryButton} onChange={(v) => update({ heroPrimaryButton: v })} />
            <Field label="Texto do botão secundário" value={texts.heroSecondaryButton} onChange={(v) => update({ heroSecondaryButton: v })} />
            <div className="md:col-span-2">
              <Field label="Imagem do banner principal (URL)" value={texts.heroImage} onChange={(v) => update({ heroImage: v })} />
            </div>
          </div>
        </div>

        {/* About */}
        <div className="bg-white rounded-xl border border-background-200 p-5">
          <h2 className="font-heading text-base font-semibold text-foreground-900 mb-4 flex items-center gap-2">
            <i className="ri-information-line text-primary-600" /> Sobre a marca
          </h2>
          <div className="space-y-4">
            <Field label="Título da seção" value={texts.aboutTitle} onChange={(v) => update({ aboutTitle: v })} />
            <Field label="Texto institucional" value={texts.aboutText} onChange={(v) => update({ aboutText: v })} textarea rows={4} />
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">Destaques da marca (um por linha)</label>
              <textarea value={texts.aboutHighlights.join('\n')} onChange={(e) => update({ aboutHighlights: e.target.value.split('\n').filter((l) => l.trim()) })} rows={4} className="w-full px-3 py-2.5 rounded-lg text-sm border border-background-300 bg-white text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none" />
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div className="bg-white rounded-xl border border-background-200 p-5">
          <h2 className="font-heading text-base font-semibold text-foreground-900 mb-4 flex items-center gap-2">
            <i className="ri-megaphone-line text-primary-600" /> Chamada final
          </h2>
          <div className="space-y-4">
            <Field label="Título" value={texts.finalCtaTitle} onChange={(v) => update({ finalCtaTitle: v })} />
            <Field label="Texto" value={texts.finalCtaText} onChange={(v) => update({ finalCtaText: v })} textarea rows={3} />
            <Field label="Texto do botão" value={texts.finalCtaButton} onChange={(v) => update({ finalCtaButton: v })} />
          </div>
        </div>

        {/* SEO */}
        <div className="bg-white rounded-xl border border-background-200 p-5 lg:col-span-2">
          <h2 className="font-heading text-base font-semibold text-foreground-900 mb-4 flex items-center gap-2">
            <i className="ri-search-eye-line text-primary-600" /> SEO básico
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Título do site (até 60 caracteres)" value={texts.seoTitle} onChange={(v) => update({ seoTitle: v })} />
            <Field label="Palavras-chave" value={texts.seoKeywords} onChange={(v) => update({ seoKeywords: v })} />
            <div className="md:col-span-2">
              <Field label="Descrição do site (120-160 caracteres)" value={texts.seoDescription} onChange={(v) => update({ seoDescription: v })} textarea rows={2} />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-6">
        <button type="button" onClick={handleSave} className="px-5 py-2.5 rounded-full text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors whitespace-nowrap">
          Salvar alterações
        </button>
      </div>
    </div>
  );
}

export default TextsPage;